import { useRequestScope } from './useRequestScope'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { fetchAdminMe } from '../api'
import { useWorkspace } from '../context/WorkspaceContext'
import { getDebugSettings, setShowTechnicalInfo, subscribeToDebugSettings } from './debugSettings'
import { TechnicalInfoContext, type TechnicalInfoContextValue } from './technicalInfoCtx'

export function TechnicalInfoProvider({ children }: { children: ReactNode }) {
  const requests = useRequestScope()
  const { ready, currentWorkspaceId } = useWorkspace()
  const [storedShowTechnicalInfo, setStoredShowTechnicalInfoState] = useState<boolean>(() => getDebugSettings().showTechnicalInfo)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [isResolved, setIsResolved] = useState(false)

  useEffect(() => {
    return subscribeToDebugSettings((next) => setStoredShowTechnicalInfoState(next.showTechnicalInfo))
  }, [])

  useEffect(() => {
    if (!ready || !currentWorkspaceId) return
    let alive = true
    const readRequest = requests.begin('read-effect-1')
    void fetchAdminMe(readRequest.signal)
      .then((me) => {
        if (!alive || !readRequest.current()) return
        setIsSuperAdmin(Boolean(me.isSuperAdmin))
        setIsResolved(true)
      })
      .catch(() => {
        if (!alive || !readRequest.current()) return
        setIsSuperAdmin(false)
        setIsResolved(true)
      })
    return () => {
      alive = false
      readRequest.cancel()
    }
  }, [ready, currentWorkspaceId, requests])

  useEffect(() => {
    if (isResolved && !isSuperAdmin && storedShowTechnicalInfo) {
      setShowTechnicalInfo(false)
    }
  }, [isResolved, isSuperAdmin, storedShowTechnicalInfo])

  const value = useMemo<TechnicalInfoContextValue>(() => {
    const showTechnicalInfo = isSuperAdmin ? storedShowTechnicalInfo : false
    return {
      isSuperAdmin,
      isResolved,
      showTechnicalInfo,
      setShowTechnicalInfo: (v: boolean) => {
        if (!isSuperAdmin) {
          if (storedShowTechnicalInfo) setShowTechnicalInfo(false)
          return
        }
        setShowTechnicalInfo(v)
      },
    }
  }, [isSuperAdmin, isResolved, storedShowTechnicalInfo])

  return <TechnicalInfoContext.Provider value={value}>{children}</TechnicalInfoContext.Provider>
}
